import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import { Delete, Edit, Add, Visibility } from "@mui/icons-material";
import RoomSharpIcon from "@mui/icons-material/RoomSharp"; // Added icon for header
import { toast } from "react-toastify";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import CustomFormDialog from "../../Components/CustomFormDialog";
import { useNavigate } from "react-router";
import { useLang } from "../../context/LanguageContext";

const StockLocationList = () => {
    const { t } = useLang();
    const authHeader = useAuthHeader();
    const [stockLocations, setStockLocations] = useState([]);
    const [isRefresh, setIsRefresh] = useState(false);

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch Stock Locations
    useEffect(() => {
        const fetchStockLocations = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(
                    `${REACT_APP_BACKEND_URL}/stock/stockLocation/get`,
                    {
                        headers: { Authorization: authHeader },
                    },
                );
                if (response.ok) {
                    const { stockLocations } = await response.json();
                    setStockLocations(stockLocations);
                } else {
                    toast.error(
                        t("FAILED_TO_FETCH_DATA") ||
                            "Failed to fetch stock locations",
                    );
                }
            } catch (error) {
                console.log(error);
                toast.error(t("NETWORK_ERROR") || "Network error occurred");
            } finally {
                setIsLoading(false);
            }
        };

        fetchStockLocations();
    }, [authHeader, isRefresh, t]);

    // Handle Delete
    const deleteStockLocation = async () => {
        const response = await fetch(
            `${REACT_APP_BACKEND_URL}/stock/stockLocation/${selectedLocationId}`,
            {
                method: "DELETE",
                headers: { Authorization: authHeader },
            },
        );
        if (response.ok) {
            toast.success(t("DELETE_LOCATION_SUCCESS"));
            setIsRefresh(!isRefresh);
        } else {
            toast.error(t("FAILED_TO_DELETE") || "Failed to delete");
        }
        setOpenDeleteDialog(false);
    };

    // Open Edit Dialog
    const handleEdit = (location) => {
        setSelectedLocation(location);
        setIsEditDialogOpen(true);
    };

    const handleAddNew = () => {
        setIsAddDialogOpen(true);
    };

    const navigate = useNavigate();
    return (
        <Box sx={{ p: 3, width: "100%" }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #00838f 0%, #006064 100%)", // Cyan/Teal gradient
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <RoomSharpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Typography variant="h5" fontWeight={700}>
                        {t("STOCK_LOCATIONS")}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddNew}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                        boxShadow: "none",
                    }}
                >
                    {t("ADD_NEW_LOCATION")}
                </Button>
            </Paper>

            {!isLoading ? (
                <TableContainer
                    component={Paper}
                    sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("NAME")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("DESCRIPTION")}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {t("ACTIONS")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stockLocations.map((location) => (
                                <TableRow key={location.id} hover>
                                    <TableCell>{location.name}</TableCell>
                                    <TableCell>
                                        {location.description}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            color="primary"
                                            onClick={() => {
                                                navigate(
                                                    "/StockLocationItems/" +
                                                        location.id,
                                                );
                                            }}
                                            title={
                                                t("VIEW_ITEMS") || "View Items"
                                            }
                                        >
                                            <Visibility />
                                        </IconButton>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEdit(location)}
                                            title={t("EDIT")}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setSelectedLocationId(
                                                    location.id,
                                                );
                                                setOpenDeleteDialog(true);
                                            }}
                                            title={t("DELETE")}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {stockLocations.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{ py: 3 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t("NO_DATA") ||
                                                "No stock locations found"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography sx={{ mt: 2, textAlign: "center" }}>
                    {t("LOADING") || "Loading..."}
                </Typography>
            )}

            {/* Add Dialog */}
            <CustomFormDialog
                title={t("ADD_STOCK_LOCATION")}
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                fields={[
                    {
                        label: t("NAME"),
                        name: "name",
                        type: "text",
                        placeholder: t("ENTER_NAME"),
                        required: true,
                        width: "49%",
                        validate: (value) =>
                            value.trim() === ""
                                ? t("FIELD_REQUIRED") || "Required"
                                : "",
                    },
                    {
                        label: t("DESCRIPTION"),
                        name: "description",
                        type: "text",
                        placeholder: t("ENTER_DESCRIPTION"),
                        required: true,
                        width: "49%",
                        multiline: true,
                    },
                ]}
                handleSubmitClick={async (formData) => {
                    const response = await fetch(
                        `${REACT_APP_BACKEND_URL}/stock/stockLocation/create`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: authHeader,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(formData),
                        },
                    );
                    if (response.ok) {
                        toast.success(t("ADD_SUCCESS") || "Added successfully");
                        setIsRefresh(!isRefresh);
                    } else {
                        toast.error(t("ADD_FAILED") || "Failed to add");
                    }
                }}
            />

            {/* Edit Dialog */}
            {selectedLocation && (
                <CustomFormDialog
                    title={t("EDIT_STOCK_LOCATION")}
                    open={isEditDialogOpen}
                    onClose={() => setIsEditDialogOpen(false)}
                    fields={[
                        {
                            label: t("NAME"),
                            name: "name",
                            type: "text",
                            placeholder: t("ENTER_NAME"),
                            required: true,
                            width: "49%",
                            value: selectedLocation.name,
                            defaultValue: selectedLocation.name,
                        },
                        {
                            label: t("DESCRIPTION"),
                            name: "description",
                            type: "text",
                            placeholder: t("ENTER_DESCRIPTION"),
                            required: true,
                            width: "49%",
                            value: selectedLocation.description,
                            multiline: true,
                            defaultValue: selectedLocation.description,
                        },
                    ]}
                    handleSubmitClick={async (formData) => {
                        const response = await fetch(
                            `${REACT_APP_BACKEND_URL}/stock/stockLocation/${selectedLocation.id}`,
                            {
                                method: "PATCH",
                                headers: {
                                    Authorization: authHeader,
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(formData),
                            },
                        );
                        if (response.ok) {
                            toast.success(
                                t("UPDATE_SUCCESS") || "Updated successfully",
                            );
                            setIsEditDialogOpen(false);
                            setIsRefresh(!isRefresh);
                        } else {
                            toast.error(
                                t("UPDATE_FAILED") || "Failed to update",
                            );
                        }
                    }}
                />
            )}

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>{t("CONFIRM")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("CONFIRM_DELETE_LOCATION")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>
                        {t("CANCEL")}
                    </Button>
                    <Button
                        onClick={deleteStockLocation}
                        color="error"
                        variant="contained"
                    >
                        {t("DELETE")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StockLocationList;
